// Typed helpers for /api/outreach + query keys (["outreach", …, igAccountId]).

import type { ID, IgUserProfile, Page, PipelineStage, Tag } from "@gf/shared";
import type {
  OutreachReview,
  OutreachAudience,
  OutreachAudienceEstimateRes,
  OutreachCampaign,
  OutreachCampaignInput,
  OutreachCampaignPatch,
  OutreachCampaignWithStats,
  OutreachOverview,
  OutreachPreviewRes,
  OutreachStage,
  OutreachStats,
  OutreachStep,
  OutreachTarget,
  OutreachTargetStatus,
  OutreachWelcomeStatus,
} from "@gf/shared/features/outreach";
import { api } from "../../lib/api";

export const outreachKeys = {
  all: ["outreach"] as const,
  campaigns: (igAccountId: ID) => ["outreach", "campaigns", igAccountId] as const,
  reviews: (id: ID) => ["outreach", "reviews", id] as const,
  campaign: (id: ID) => ["outreach", "campaign", id] as const,
  targets: (id: ID, status: string) => ["outreach", "targets", id, status] as const,
  overview: (igAccountId: ID) => ["outreach", "overview", igAccountId] as const,
  welcome: (igAccountId: ID) => ["outreach", "welcome-status", igAccountId] as const,
  stages: ["outreach", "crm-stages"] as const,
  tags: ["outreach", "crm-tags"] as const,
};

export const outreachApi = {
  reviews: (id: ID, cursor: string | null) => api<Page<OutreachReview>>(`/api/outreach/campaigns/${id}/reviews`, { query: { cursor, limit: 30 } }),
  approveReview: (id: ID, review: OutreachReview, text: string) => api<OutreachReview>(`/api/outreach/campaigns/${id}/reviews/${review.id}/approve`, { method: "POST", body: { text, igUserId: review.igUserId, updatedAt: review.updatedAt } }),
  regenerateReview: (id: ID, review: OutreachReview) => api<OutreachReview>(`/api/outreach/campaigns/${id}/reviews/${review.id}/regenerate`, { method: "POST", body: { igUserId: review.igUserId, updatedAt: review.updatedAt } }),
  skipReview: (id: ID, reviewId: ID) => api<{ ok: true }>(`/api/outreach/campaigns/${id}/reviews/${reviewId}/skip`, { method: "POST", body: {} }),
  campaigns: (igAccountId: ID) => api<{ items: OutreachCampaignWithStats[] }>("/api/outreach/campaigns", { query: { igAccountId } }),
  campaign: (id: ID) => api<{ campaign: OutreachCampaignWithStats }>(`/api/outreach/campaigns/${id}`),
  create: (body: OutreachCampaignInput) => api<{ campaign: OutreachCampaign }>("/api/outreach/campaigns", { method: "POST", body }),
  update: (id: ID, body: OutreachCampaignPatch) => api<{ campaign: OutreachCampaign }>(`/api/outreach/campaigns/${id}`, { method: "PATCH", body }),
  remove: (id: ID) => api<{ ok: true }>(`/api/outreach/campaigns/${id}`, { method: "DELETE" }),
  setStatus: (id: ID, status: "active" | "paused" | "completed") =>
    api<{ campaign: OutreachCampaignWithStats; added: number }>(`/api/outreach/campaigns/${id}/status`, { method: "POST", body: { status } }),
  duplicate: (id: ID) => api<{ campaign: OutreachCampaign }>(`/api/outreach/campaigns/${id}/duplicate`, { method: "POST", body: {} }),
  stats: (id: ID) => api<OutreachStats>(`/api/outreach/campaigns/${id}/stats`),
  targets: (id: ID, stage: OutreachStage | "all", status: OutreachTargetStatus | "all" | "followed" | "unfollowed" | "following_you", cursor: string | null) =>
    api<Page<OutreachTarget>>(`/api/outreach/campaigns/${id}/targets`, { query: { stage: stage === "all" ? undefined : stage, status: status === "all" || status === "unfollowed" || status === "followed" || status === "following_you" ? undefined : status, unfollowed: status === "unfollowed" ? "true" : undefined, followingYou: status === "following_you" ? "true" : undefined, followed: status === "followed" ? "true" : undefined, cursor, limit: 50 } }),
  targetAction: (id: ID, targetId: ID, action: "skip" | "retry") =>
    api<{ target: OutreachTarget }>(`/api/outreach/campaigns/${id}/targets/${targetId}`, { method: "PATCH", body: { action } }),
  preview: (body: { igAccountId?: string; steps: OutreachStep[]; ai?: { goal: string; offer: string }; step?: number; sampleProfile?: IgUserProfile }) =>
    api<OutreachPreviewRes>("/api/outreach/preview", { method: "POST", body }),
  estimate: (igAccountId: ID, audience: OutreachAudience) =>
    api<OutreachAudienceEstimateRes>("/api/outreach/audience/estimate", { method: "POST", body: { igAccountId, audience } }),
  overview: (igAccountId: ID) => api<OutreachOverview>("/api/outreach/overview", { query: { igAccountId } }),
  welcomeStatus: (igAccountId: ID) => api<OutreachWelcomeStatus>("/api/outreach/welcome-status", { query: { igAccountId } }),
  // CRM contract (fixed in @gf/shared api.ts) — read-only here.
  stages: () => api<{ items: PipelineStage[] }>("/api/crm/stages"),
  tags: () => api<{ items: Tag[] }>("/api/crm/tags"),
};
